import {useToastContext} from '@github-ui/toast/ToastContext'
import {IssueClosedIcon, IssueReopenedIcon, SkipIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, IconButton, Text} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import type {IssueActions$key} from './__generated__/IssueActions.graphql'
import type {IssueActionsPermissions$key} from './__generated__/IssueActionsPermissions.graphql'
import type {IssueActionsState$key, IssueState, IssueStateReason} from './__generated__/IssueActionsState.graphql'
import {BUTTON_LABELS} from './constants/button-labels'
import {ERRORS} from './constants/errors'
import {LABELS} from './constants/labels'
import type {IssueClosedStateReason} from './mutations/__generated__/updateIssueStateMutationCloseMutation.graphql'
import {commitCloseIssueMutation, commitReopenIssueMutation} from './mutations/update-issue-state-mutation'

export type CloseButtonState = 'CLOSED' | 'OPEN' | 'NOT_PLANNED'

type IssueActionsBaseProps = {
  onAction: () => void
  hasComment: boolean
  buttonSize?: 'small' | 'medium'
  closeButtonState: CloseButtonState
  setCloseButtonState: (state: CloseButtonState) => void
}

type InternalIssueActionsProps = IssueActionsBaseProps & {
  actionStateRef: IssueActionsState$key
  viewerCanClose: boolean
  viewerCanReopen: boolean
  buttonSize?: 'small' | 'medium'
}

type IssueActionsProps = IssueActionsBaseProps & {
  actionRef: IssueActions$key
}

export function IssueActions({actionRef, ...rest}: IssueActionsProps) {
  const action = useFragment(
    graphql`
      fragment IssueActions on Issue {
        ...IssueActionsPermissions
        ...IssueActionsState
      }
    `,
    actionRef,
  )

  const {viewerCanClose, viewerCanReopen} = useFragment<IssueActionsPermissions$key>(
    graphql`
      fragment IssueActionsPermissions on Issue {
        viewerCanClose
        viewerCanReopen
      }
    `,
    action,
  )

  if (!viewerCanClose && !viewerCanReopen) return null

  return (
    <IssueActionsInternal
      actionStateRef={action}
      {...rest}
      viewerCanClose={viewerCanClose}
      viewerCanReopen={viewerCanReopen}
    />
  )
}

function IssueActionsInternal({
  actionStateRef,
  onAction,
  hasComment,
  viewerCanClose,
  viewerCanReopen,
  buttonSize,
  closeButtonState,
  setCloseButtonState,
}: InternalIssueActionsProps) {
  const {addToast} = useToastContext()
  const data = useFragment(
    graphql`
      fragment IssueActionsState on Issue {
        state
        stateReason
        id
      }
    `,
    actionStateRef,
  )

  const environment = useRelayEnvironment()

  const closeIssue = useCallback(
    (newStateReason: IssueClosedStateReason) => {
      if (data && data.id) {
        return commitCloseIssueMutation({
          environment,
          input: {
            issueId: data.id,
            newStateReason,
          },
          onError: () =>
            addToast({
              type: 'error',
              message: ERRORS.couldNotCloseIssue,
            }),
        })
      }
    },
    [data, environment, addToast],
  )

  const reopenIssue = useCallback(() => {
    if (data.id) {
      commitReopenIssueMutation({
        environment,
        input: {
          issueId: data.id,
        },
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotReOpenIssue,
          }),
      })
    }
  }, [data, environment, addToast])

  useEffect(
    () => setCloseButtonState(getInitialButtonState(data.state)),
    [data.state, data.stateReason, data.id, setCloseButtonState],
  )
  const [open, setOpen] = useState(false)

  return (
    <>
      <ButtonGroup sx={{display: 'flex', justifyContent: 'flex-end'}}>
        <UpdateStateButton
          issueState={data.state}
          buttonState={closeButtonState}
          onClose={closeIssue}
          onReopen={reopenIssue}
          onAction={onAction}
          hasComment={hasComment}
          size={buttonSize}
        />
        <ActionMenu open={open} onOpenChange={setOpen}>
          <ActionMenu.Anchor>
            <IconButton icon={TriangleDownIcon} aria-label={LABELS.moreOptions} size={buttonSize} />
          </ActionMenu.Anchor>

          <ActionMenu.Overlay width="medium">
            <UpdateStateButtonOptions
              buttonState={closeButtonState}
              setButtonState={setCloseButtonState}
              canClose={closeIssue !== undefined}
              canReopen={reopenIssue !== undefined}
              viewerCanClose={viewerCanClose}
              viewerCanReopen={viewerCanReopen}
              issueState={data.state}
              issueStateReason={data.stateReason}
            />
          </ActionMenu.Overlay>
        </ActionMenu>
      </ButtonGroup>
    </>
  )
}

type UpdateStatusButtonProps = {
  issueState: IssueState
  buttonState: CloseButtonState
  onClose?: (reason: IssueClosedStateReason) => void
  onReopen?: () => void
  onAction?: () => void
  hasComment: boolean
  size?: 'small' | 'medium'
}

const UpdateStateButton: React.FC<UpdateStatusButtonProps> = ({
  issueState,
  buttonState,
  onClose,
  onReopen,
  onAction,
  hasComment = false,
  size = 'medium',
}: UpdateStatusButtonProps) => {
  if (issueState === 'CLOSED') {
    if (buttonState === 'OPEN') {
      return (
        <Button
          onClick={() => {
            onAction?.()
            onReopen?.()
          }}
          leadingVisual={IssueReopenedIcon}
          sx={{color: 'open.fg'}}
          disabled={!onReopen}
          size={size}
        >
          <Text sx={{color: 'fg.default'}}>{LABELS.reOpenIssue}</Text>
        </Button>
      )
    }

    if (buttonState === 'NOT_PLANNED') {
      return (
        <Button
          onClick={() => {
            onAction?.()
            onClose?.('NOT_PLANNED')
          }}
          leadingVisual={SkipIcon}
          sx={{color: 'fg.muted'}}
          disabled={!onClose}
          size={size}
        >
          <Text sx={{color: 'fg.default'}}>{BUTTON_LABELS.closeIssueNotPlanned}</Text>
        </Button>
      )
    }

    if (buttonState === 'CLOSED') {
      return (
        <Button
          onClick={() => {
            onAction?.()
            onClose?.('COMPLETED')
          }}
          leadingVisual={IssueClosedIcon}
          sx={{color: 'done.fg'}}
          disabled={!onClose}
          size={size}
        >
          <Text sx={{color: 'fg.default'}}>{BUTTON_LABELS.closeIssueCompleted}</Text>
        </Button>
      )
    }
  }

  if (issueState === 'OPEN') {
    if (buttonState === 'NOT_PLANNED') {
      return (
        <Button
          onClick={() => {
            onAction?.()
            onClose?.('NOT_PLANNED')
          }}
          leadingVisual={SkipIcon}
          sx={{color: 'fg.muted'}}
          disabled={!onClose}
          size={size}
        >
          <Text sx={{color: 'fg.default'}}>
            {hasComment ? BUTTON_LABELS.closeIssueWithComment : BUTTON_LABELS.closeIssue}
          </Text>
        </Button>
      )
    }

    if (buttonState === 'CLOSED') {
      return (
        <Button
          onClick={() => {
            onAction?.()
            onClose?.('COMPLETED')
          }}
          leadingVisual={IssueClosedIcon}
          sx={{color: 'done.fg'}}
          disabled={!onClose}
          size={size}
        >
          <Text sx={{color: 'fg.default'}}>
            {hasComment ? BUTTON_LABELS.closeIssueWithComment : BUTTON_LABELS.closeIssue}
          </Text>
        </Button>
      )
    }
  }

  return null
}

type UpdateStateButtonOptionsProps = {
  buttonState: CloseButtonState
  setButtonState: (state: CloseButtonState) => void
  issueState: IssueState
  issueStateReason: IssueStateReason | null | undefined
  canClose: boolean
  canReopen: boolean
  viewerCanClose: boolean
  viewerCanReopen: boolean
}

const UpdateStateButtonOptions: React.FC<UpdateStateButtonOptionsProps> = ({
  buttonState,
  setButtonState,
  issueState,
  issueStateReason,
  canClose,
  canReopen,
  viewerCanClose,
  viewerCanReopen,
}: UpdateStateButtonOptionsProps) => (
  <ActionList
    selectionVariant="single"
    aria-roledescription={LABELS.updateIssueRoleDescription}
    sx={{
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {issueState === 'CLOSED' && viewerCanReopen && (
      <ActionList.Item
        key={'reopen-issue-option'}
        onSelect={() => setButtonState('OPEN')}
        selected={buttonState === 'OPEN'}
        disabled={!canReopen}
      >
        <ActionList.LeadingVisual sx={{color: 'open.fg'}}>
          <IssueReopenedIcon />
        </ActionList.LeadingVisual>
        {LABELS.reOpenIssue}
      </ActionList.Item>
    )}
    {(issueState === 'OPEN' || issueStateReason === 'NOT_PLANNED') && viewerCanClose && (
      <ActionList.Item
        key={'close-issue-option'}
        onSelect={() => setButtonState('CLOSED')}
        selected={buttonState === 'CLOSED'}
        disabled={!canClose}
      >
        <ActionList.LeadingVisual sx={{color: 'done.fg'}}>
          <IssueClosedIcon />
        </ActionList.LeadingVisual>
        {LABELS.closeAsCompleted}
        <ActionList.Description variant="block">{LABELS.actionListCompletedDescription}</ActionList.Description>
      </ActionList.Item>
    )}
    {(issueState === 'OPEN' || (issueState === 'CLOSED' && issueStateReason !== 'NOT_PLANNED')) && viewerCanClose && (
      <ActionList.Item
        key={'skip-issue-option'}
        onSelect={() => setButtonState('NOT_PLANNED')}
        selected={buttonState === 'NOT_PLANNED'}
        disabled={!canClose}
      >
        <ActionList.LeadingVisual>
          <SkipIcon />
        </ActionList.LeadingVisual>
        {LABELS.closeAsNotPlanned}
        <ActionList.Description variant="block">{LABELS.actionListNotPlannedDescription}</ActionList.Description>
      </ActionList.Item>
    )}
  </ActionList>
)

const getInitialButtonState = (state: IssueState) => {
  return state === 'CLOSED' ? 'OPEN' : 'CLOSED'
}

try{ IssueActions.displayName ||= 'IssueActions' } catch {}
try{ IssueActionsInternal.displayName ||= 'IssueActionsInternal' } catch {}
try{ UpdateStateButton.displayName ||= 'UpdateStateButton' } catch {}
try{ UpdateStateButtonOptions.displayName ||= 'UpdateStateButtonOptions' } catch {}